import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Alert, Button as AntdButton, Button, Card, Dropdown, Input, Modal, Select, Switch, Table, Tabs, Tag } from "antd";
import { PrinterOutlined, PlusOutlined, CloseCircleOutlined, FileTextOutlined, DollarOutlined, CheckCircleOutlined, MoreOutlined, CalendarOutlined, FilePdfOutlined, FileExcelOutlined } from "@ant-design/icons";
import { useReactToPrint } from "react-to-print";

import QuittanceIndemnite from "../../../docs/QuittanceIndemnite";
import OrdreDeDepense from "../../../docs/OrdreDeDepense";
import FicheDemandeReglementSinistre from "../../../docs/FicheDemandeReglementSinistre";
import AssignCheque from "../../../components/AssignCheque";
import RemiseCheque from "../../../components/RemiseCheque";
import AnnulerCheque from "../../../components/AnnulerCheque";
import ReglementModal from "../../../components/modals/ReglementModal";
import VentilationGarantieModal from "../../../components/modals/VentilationGarantieModal";
import EditDecaissementModal from "../../../components/modals/EditDecaissementModal";
import EditMotifReglementModal from "../../../components/modals/EditMotifReglementModal";
import { rightsService } from "../../../../../services/rightsService";
import axiosInstance from "../../../../../variables/api/axiosInstance";
import { displayNumbers } from "../../../../../variables/numbersFunctions";
import { frenchDate } from "../../../../../variables/functions";
import { toast } from "react-toastify";
import { rejectDecaissement, validateDecaissement, validateDecaissementsGroupe, annulerReglementTechnique, annulerReglementTechniqueGroupe, updateMotifReglement, updateMotifReglementGroupe, getParametre } from "../../../../../hooks/useHooks";
import { PARAMETERS } from "../../../../../variables/constants";
import FixEnginCategorieModal from "../../../components/FixEnginCategorieModal";
import AntidatageDateReglement, { messageRefusAntidatage } from "../../../components/AntidatageDateReglement";
import { generateReglementsExcel, generateReglementsPDF } from "../../../../../hooks/useExportsSinistre";
import { aggregateGroupe, groupDecaissements, natureLabel } from "../../../docs/groupeReglement";

// Refonte UI du tableau (mode variant="v2") : réécrit uniquement la PRÉSENTATION AntD Table
// (en-tête, lignes aérées, survol, pastilles, cartes) — aucune logique/handler/modale modifiée.
const RGTAB_V2_CSS = `
.rgtab-v2 .ant-card{border-radius:14px;border:1px solid #E7E8F2;box-shadow:0 1px 2px rgba(24,26,44,.04),0 6px 20px -10px rgba(24,26,44,.14)}
.rgtab-v2 .ant-card-body{padding:16px}
.rgtab-v2 .ant-tabs-nav{margin-bottom:10px}
.rgtab-v2 .ant-tabs-tab{font-weight:600;color:#636A85;border-radius:9px !important}
.rgtab-v2 .ant-tabs-tab.ant-tabs-tab-active{background:#EDECF8 !important;border-color:transparent !important}
.rgtab-v2 .ant-tabs-tab.ant-tabs-tab-active .ant-tabs-tab-btn{color:#242166 !important}
.rgtab-v2 .ant-table{font-size:13px;background:transparent}
.rgtab-v2 .ant-table-thead > tr > th{background:#F7F8FC !important;color:#9BA0B8 !important;font-size:11px;font-weight:700;letter-spacing:.04em;text-transform:uppercase;border-bottom:1px solid #E7E8F2 !important;padding:11px 16px !important}
.rgtab-v2 .ant-table-thead > tr > th::before{display:none !important}
.rgtab-v2 .ant-table-tbody > tr > td{border-bottom:1px solid #EFF0F7 !important;padding:14px 16px !important;transition:background .12s;vertical-align:middle}
.rgtab-v2 .ant-table-tbody > tr:hover > td{background:#FAFAFE !important}
.rgtab-v2 .ant-table-tbody > tr.group-row > td{background:#FAF9FF !important}
.rgtab-v2 .ant-table-tbody > tr:last-child > td{border-bottom:none !important}
.rgtab-v2 .ant-tag{border-radius:999px;padding:3px 11px;font-weight:600;font-size:11.5px;border:none}
.rgtab-v2 .ant-pagination{margin-top:16px !important}
.rgtab-v2 .ant-table-row-expand-icon{border-radius:6px;border-color:#D8DAE8}
.rgtab-v2 .ant-table-column-sort{background:transparent !important}
.rgtab-v2 .rp{display:inline-flex;align-items:center;gap:6px;font-size:12px;font-weight:650;padding:4px 11px;border-radius:999px;line-height:1;white-space:nowrap}
.rgtab-v2 .rp .d{width:6px;height:6px;border-radius:50%;background:currentColor}
.rgtab-v2 .rp.ok{color:#0B7C55;background:#E4F5EE}
.rgtab-v2 .rp.warn{color:#9A5E06;background:#FBF0DC}
.rgtab-v2 .rp.crit{color:#B91C1C;background:#FBE7E7}
.rgtab-v2 .rp.info{color:#1D4FD8;background:#E6EDFD}
.rgtab-v2 .rp.default{color:#636A85;background:#EEF0F5}
.rgtab-v2 .rbenef{display:flex;align-items:center;gap:10px}
.rgtab-v2 .rbenef .av{width:34px;height:34px;border-radius:9px;display:grid;place-items:center;font-size:13px;font-weight:700;background:#EDECF8;color:#242166;flex-shrink:0}
.rgtab-v2 .rbenef .av.grp{background:#EFE9FB;color:#7c3aed}
.rgtab-v2 .rbenef .av.neg{background:#FBE7E7;color:#DC2626}
.rgtab-v2 .rbenef .nm{font-weight:650;color:#181A2C;display:flex;align-items:center;gap:6px;flex-wrap:wrap}
.rgtab-v2 .rbenef .rg{font-size:11px;color:#9BA0B8;font-family:ui-monospace,Menlo,monospace;margin-top:2px}
.rgtab-v2 .rmoney{font-weight:700;font-variant-numeric:tabular-nums}
.rgtab-v2 .rmoney .cur{font-size:11px;color:#9ca3af;font-weight:400}
`;

// États des décaissements → pilule V2 (libellé propre sans emoji + variante couleur).
const RGTAB_V2_STATE = {
    VALIDE: { label: "Validé", cls: "info" },
    EN_ATTENTE: { label: "En attente", cls: "default" },
    EN_ATTENTE_D_ENCAISSEMENT: { label: "En attente d'encaissement", cls: "warn" },
    ENCAISSE: { label: "Encaissé", cls: "ok" },
    CHEQUE_ANNULE: { label: "Chèque annulé", cls: "crit" },
    REJETE: { label: "Rejeté", cls: "default" },
    GROUPE: { label: "États mixtes", cls: "info" },
};

const ReglementsTab = ({
    sinistre,
    decaissements,
    evaluationCourante,
    modalQuittance,
    setModalQuittance,
    quittance,
    setQuittance,
    modalFicheDemandeReglement,
    setModalFicheDemandeReglement,
    ficheDemandeReglement,
    setFicheDemandeReglement,
    modalAssignCheque,
    setModalAssignCheque,
    modalRemiseCheque,
    setModalRemiseCheque,
    modalAnnulerCheque,
    setModalAnnulerCheque,
    decaissement,
    setDecaissement,
    montant,
    setRefetch,
    onDecaissementUpdated,
    variant,
}) => {
    const navigate = useNavigate();
    const isV2 = variant === "v2";
    const readOnly = !!sinistre?.lectureSeule;
    const [modalOrdreDepense, setModalOrdreDepense] = useState(false);
    const [ordreDepense, setOrdreDepense] = useState({});
    // Saisie du motif du règlement depuis l'ordre de dépense (règlements multiples surtout).
    const [odMotif, setOdMotif] = useState("");
    const [savingOdMotif, setSavingOdMotif] = useState(false);
    const printQReference = useRef(null);
    const printFDReference = useRef(null);
    const printODReference = useRef(null);

    const handleQPrint = useReactToPrint({ content: () => printQReference.current });

    const exportQToPdf = () => {
        handleQPrint();
        setTimeout(() => {
            if (quittance?.codeId) {
                axiosInstance
                    .put(`/api/decaissement/print/${quittance.codeId}`)
                    .catch(() => { /* ignore */ });
            }
        }, 500);
    };

    const exportFDRToPdf = useReactToPrint({ content: () => printFDReference.current });
    const exportODToPdf = useReactToPrint({ content: () => printODReference.current });

    const [reglementModalOpen, setReglementModalOpen] = useState(false);
    const [editDecaissementId, setEditDecaissementId] = useState(null);
    // Règlement validé : modification du seul motif, sous habilitation HSI089.
    const [editMotifRecord, setEditMotifRecord] = useState(null);
    const canEditMotif = rightsService("HSI089");
    // Annulation technique d'un règlement (ligne négative) — sous habilitation HSI015.
    const canAnnulerTech = rightsService("HSI015");

    // Fenêtre d'annulation technique (motif OBLIGATOIRE : liste MOTIF + précision libre).
    const [techCancel, setTechCancel] = useState({ open: false, mode: null, record: null });
    const [techMotif, setTechMotif] = useState(undefined);
    const [techPrecision, setTechPrecision] = useState("");
    const [techSaving, setTechSaving] = useState(false);
    const [techMotifs, setTechMotifs] = useState([]);
    useEffect(() => {
        if (!canAnnulerTech) return;
        getParametre(PARAMETERS.motifReglement)
            .then((r) => {
                const seen = new Set(); const out = [];
                (r?.data || []).forEach((m) => {
                    const label = m?.libelleParametre || m?.valeur || "";
                    const value = m?.valeur || m?.libelleParametre || "";
                    const k = String(label).trim().toLowerCase();
                    if (!k || seen.has(k)) return; seen.add(k); out.push({ label, value });
                });
                setTechMotifs(out);
            })
            .catch(() => setTechMotifs([]));
    }, [canAnnulerTech]);

    const openTechCancel = (record, mode) => {
        if (readOnly) { toast.warn("Dossier en lecture seule : action impossible."); return; }
        setTechMotif(undefined); setTechPrecision("");
        setTechCancel({ open: true, mode, record });
    };
    const confirmTechCancel = async () => {
        const motifFinal = [techMotif, techPrecision?.trim()].filter(Boolean).join(" — ");
        if (!motifFinal) { toast.warn("Le motif de l'annulation est obligatoire."); return; }
        const { mode, record } = techCancel;
        setTechSaving(true);
        try {
            if (mode === "group") {
                await annulerReglementTechniqueGroupe(record.groupeReglementCodeId, motifFinal);
                toast.success("Groupe annulé techniquement (lignes négatives créées).");
            } else {
                await annulerReglementTechnique(record.codeId, motifFinal);
                toast.success("Règlement annulé (ligne négative créée).");
            }
            setTechCancel({ open: false, mode: null, record: null });
            window.location.reload();
        } catch (e) {
            console.log(e);
            toast.error(e?.response?.data?.message || "Erreur lors de l'annulation technique.");
        } finally { setTechSaving(false); }
    };
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [ventilationOpen, setVentilationOpen] = useState(false);
    const [ventilationTarget, setVentilationTarget] = useState(null);
    const [fixCategorieData, setFixCategorieData] = useState(null);

    // Par défaut, les règlements REJETÉS sont masqués (consultables via l'onglet
    // « Historique » ou en activant l'interrupteur ci-dessous).
    const [showRejected, setShowRejected] = useState(false);

    // Sous-onglet de paiement : « en-cours » (ordres de dépense non encore payés →
    // assignation du chèque + suite du règlement) sélectionné par défaut, ou « payés ».
    const [paymentTab, setPaymentTab] = useState("en-cours");

    const rejectedCount = useMemo(
        () => (decaissements || []).filter((d) => d?.etatDecaissement === "REJETE").length,
        [decaissements]
    );

    // Ligne « vide » = ni montant TTC ni montant retenu (0/0) : sous-lignes fantômes d'un règlement
    // groupé (souvent restées VALIDE à 0) qui n'ont aucune valeur à payer. Elles polluaient l'ÉTAT
    // COMMUN du groupe (→ « États mixtes ») et son classement en-cours/payés. On les masque partout.
    // (Les contre-passations sont NÉGATIVES, pas nulles → jamais masquées.)
    const estLigneVide = (d) =>
        (parseFloat(d?.montantGlobal) || 0) === 0 && (parseFloat(d?.taxes) || 0) === 0;

    const visibleDecaissements = useMemo(
        () => (decaissements || []).filter(
            (d) => (showRejected || d?.etatDecaissement !== "REJETE") && !estLigneVide(d)
        ),
        [decaissements, showRejected]
    );

    // Groupe sélectionné pour l'assignation d'un chèque unique.
    const [assignChequeGroup, setAssignChequeGroup] = useState(null);
    // true = on MODIFIE le chèque d'un règlement déjà en attente d'encaissement (HSI089) ;
    // false = assignation initiale.
    const [chequeEditMode, setChequeEditMode] = useState(false);

    // Source de données « arborescente » : les décaissements partageant un même
    // groupeReglementCodeId sont regroupés sous une ligne-mère pliable (total +
    // état commun) ; les règlements isolés restent des lignes simples.
    const groupedDataSource = useMemo(() => {
        const sum = (lignes, field) => lignes.reduce((acc, d) => acc + (parseFloat(d?.[field]) || 0), 0);
        return groupDecaissements(visibleDecaissements).map(({ key, lignes }) => {
            if (lignes.length === 1 && !lignes[0]?.groupeReglementCodeId) {
                return lignes[0]; // règlement isolé → ligne simple inchangée
            }
            const first = lignes[0];
            const etatCommun = lignes.every((d) => d?.etatDecaissement === first?.etatDecaissement)
                ? first?.etatDecaissement
                : "GROUPE";
            return {
                codeId: key,
                isGroupParent: true,
                groupeReglementCodeId: first?.groupeReglementCodeId,
                beneficiaire: first?.beneficiaire,
                numero: first?.numero,
                numeroCheque: first?.numeroCheque,
                banque: first?.banque,
                motifReglement: first?.motifReglement,
                motifAnnulerCheque: first?.motifAnnulerCheque,
                montantGlobal: sum(lignes, "montantGlobal"),
                taxes: sum(lignes, "taxes"),
                dateReglement: first?.dateReglement,
                dateEmissionCheque: first?.dateEmissionCheque,
                etatDecaissement: etatCommun,
                _lignes: lignes,
                children: lignes.map((l) => ({ ...l, isGroupChild: true })),
            };
        });
    }, [visibleDecaissements]);

    // Scission « en cours de paiement » / « payés ». Est « payé » tout règlement (ou groupe à
    // état commun) ayant atteint le stade chèque :
    //   • ENCAISSE ;
    //   • CHEQUE_ANNULE → on garde la ligne positive ET sa contre-passation négative dans « Payés » ;
    //   • EN_ATTENTE_D_ENCAISSEMENT AVEC un numéro de chèque (chèque émis, pas encore encaissé).
    // Le reste (EN_ATTENTE, VALIDE, groupes à états mixtes sans chèque) reste « en cours ».
    const estPaye = (r) => {
        const etat = r?.etatDecaissement;
        return (
            etat === "ENCAISSE" ||
            etat === "CHEQUE_ANNULE" ||
            (etat === "EN_ATTENTE_D_ENCAISSEMENT" && !!r?.numeroCheque)
        );
    };
    const enCoursData = useMemo(
        () => groupedDataSource.filter((r) => !estPaye(r)),
        [groupedDataSource]
    );
    const payesData = useMemo(
        () => groupedDataSource.filter((r) => estPaye(r)),
        [groupedDataSource]
    );
    const sumMontant = (rows) => rows.reduce((acc, r) => acc + (parseFloat(r?.montantGlobal) || 0), 0);

    const reglementControl = useMemo(
        () => ({
            open: reglementModalOpen,
            setOpen: setReglementModalOpen,
            handleSubmit: () => { },
        }),
        [reglementModalOpen]
    );
    const handleValiderDecaissement = (record) => {
        if (readOnly) { toast.warn("Dossier en lecture seule : action impossible."); return; }
        const dateAntidatage = { current: null };
        Modal.confirm({
            title: "Voulez-vous vraiment valider ce décaissement ?",
            icon: <CheckCircleOutlined style={{ color: "green", fontSize: "24px" }} />,
            width: 800,
            content: <AntidatageDateReglement dateRef={dateAntidatage} />,
            async onOk() {
                try {
                    await validateDecaissement(record.codeId, dateAntidatage.current);
                    toast.success('Décaissement validé avec succès');
                    window.location.reload();
                } catch (e) {
                    const data = e?.response?.data;
                    if (e?.response?.status === 422 && data?.code === "ENGIN_CATEGORIE_MANQUANTE") {
                        if (data.enginCodeId) {
                            setFixCategorieData({
                                enginCodeId: data.enginCodeId,
                                immatriculation: data.immatriculation,
                                decaissementCodeId: record.codeId,
                                dateReglement: dateAntidatage.current,
                            });
                        } else {
                            toast.warning(data.message || "Aucun véhicule n'est rattaché à ce sinistre. Rattachez le véhicule concerné avant de régler.");
                        }
                    } else if (messageRefusAntidatage(e)) {
                        toast.error(messageRefusAntidatage(e));
                    } else {
                        console.log(e);
                        toast.error('Erreur lors de la validation du décaissement');
                    }
                }
            },
            onCancel() {
                console.log("Validation annulée");
            },
        });
    };

    const handleRejeterDecaissement = (record) => {
        if (readOnly) { toast.warn("Dossier en lecture seule : action impossible."); return; }
        Modal.confirm({
            title: `${record?.etatDecaissement === "EN_ATTENTE" ? "Voulez-vous vraiment rejeter ce décaissement ?" : "Voulez-vous vraiment annuler ce reglement ?"}`,
            icon: <CloseCircleOutlined style={{ color: "darkred", fontSize: "24px" }} />,
            width: 800,
            async onOk() {
                try {
                    console.log("Décaissement rejeté:", record);
                    await rejectDecaissement(record.codeId);
                    toast.success('Décaissement rejeté avec succès');
                    window.location.reload();
                } catch (e) {
                    console.log(e);
                    toast.error('Erreur lors du rejet du décaissement');
                }
            },
            onCancel() {
                console.log("Rejet annulé");
            },
        });
    };

    // Annulation TECHNIQUE (HSI015) : crée la ligne négative et passe l'original en REJETE, sans
    // écriture comptable. Typiquement après annulation du chèque.
    const handleAnnulerReglementTechnique = (record) => openTechCancel(record, "line");

    // Annulation TECHNIQUE d'un GROUPE (HSI015) : applique la contre-écriture à chaque ligne du groupe
    // et les passe en REJETÉ. Refus côté back si un chèque est encore actif (à annuler d'abord).
    const handleAnnulerReglementTechniqueGroupe = (record) => openTechCancel(record, "group");

    // ---- Règlement groupé : actions au niveau du groupe ----

    const handleValiderGroupe = (record) => {
        if (readOnly) { toast.warn("Dossier en lecture seule : action impossible."); return; }
        const dateAntidatage = { current: null };
        Modal.confirm({
            title: "Valider tous les règlements de ce groupe ?",
            icon: <CheckCircleOutlined style={{ color: "green", fontSize: "24px" }} />,
            content: (
                <div>
                    Un numéro d'ordre unique sera attribué à l'ensemble des lignes.
                    <AntidatageDateReglement dateRef={dateAntidatage} />
                </div>
            ),
            width: 700,
            async onOk() {
                try {
                    await validateDecaissementsGroupe(record.groupeReglementCodeId, dateAntidatage.current);
                    toast.success("Groupe validé avec succès");
                    window.location.reload();
                } catch (e) {
                    const data = e?.response?.data;
                    if (e?.response?.status === 422 && data?.code === "ENGIN_CATEGORIE_MANQUANTE") {
                        toast.warning(data.message || "Le véhicule du sinistre doit être rattaché/catégorisé avant de régler.");
                    } else if (e?.response?.status === 422 && data?.code === "SAP_DEPASSE") {
                        // Plafond SAP dépassé : on propose d'aller DIRECTEMENT réévaluer le sinistre
                        // concerné (ou voir son détail) pour accélérer la correction.
                        const scid = data?.sinistreCodeId || record?.sinistreCodeId || record?.sinistre?.codeId;
                        Modal.confirm({
                            title: "Plafond SAP dépassé",
                            content: (
                                <div>
                                    <p>{data.message || "Le montant à régler dépasse le reste disponible du sinistre."}</p>
                                    <p style={{ marginBottom: 0 }}>Réévaluez le sinistre pour augmenter la provision, ou ouvrez son détail.</p>
                                </div>
                            ),
                            okText: "Réévaluer le sinistre",
                            cancelText: "Voir le sinistre",
                            onOk: () => navigate("/details-sinistre/v2", { state: { codeId: scid, openReeval: true } }),
                            onCancel: () => navigate("/details-sinistre/v2", { state: { codeId: scid } }),
                        });
                    } else if (messageRefusAntidatage(e)) {
                        toast.error(messageRefusAntidatage(e));
                    } else {
                        console.log(e);
                        toast.error("Erreur lors de la validation du groupe");
                    }
                }
            },
            onCancel() { },
        });
    };

    const handleRejeterGroupe = (record) => {
        if (readOnly) { toast.warn("Dossier en lecture seule : action impossible."); return; }
        Modal.confirm({
            title: "Rejeter / annuler tous les règlements de ce groupe ?",
            icon: <CloseCircleOutlined style={{ color: "darkred", fontSize: "24px" }} />,
            width: 700,
            async onOk() {
                try {
                    // On ne rejette QUE les lignes EN ATTENTE (les lignes déjà validées/payées d'un
                    // groupe mixte ne doivent pas être rejetées par un rejet simple).
                    const aRejeter = (record._lignes || []).filter((l) => l?.etatDecaissement === "EN_ATTENTE");
                    if (!aRejeter.length) { toast.warn("Aucune ligne en attente à rejeter dans ce groupe."); return; }
                    for (const ligne of aRejeter) {
                        await rejectDecaissement(ligne.codeId);
                    }
                    toast.success("Groupe rejeté avec succès");
                    window.location.reload();
                } catch (e) {
                    console.log(e);
                    toast.error("Erreur lors du rejet du groupe");
                }
            },
            onCancel() { },
        });
    };

    const handleGroupMenuClick = (e, record) => {
        // Gel total : seules les actions de LECTURE (documents) restent possibles en lecture seule.
        const readOnlyKeysGroup = ["ordreDepenseGroup", "ficheGroup", "quittanceGroup"];
        if (readOnly && !readOnlyKeysGroup.includes(e.key)) {
            toast.warn("Dossier en lecture seule : action impossible.");
            return;
        }
        switch (e.key) {
            case "validateGroup":
                handleValiderGroupe(record);
                break;
            case "rejectGroup":
                handleRejeterGroupe(record);
                break;
            case "annulerReglementTechniqueGroupe":
                handleAnnulerReglementTechniqueGroupe(record);
                break;
            case "assignChequeGroup": {
                // On n'assigne le chèque qu'aux lignes VALIDÉES (non encore payées) du groupe.
                const aAssigner = (record._lignes || []).filter((l) => l?.etatDecaissement === "VALIDE");
                if (!aAssigner.length) { toast.warn("Aucune ligne validée à payer dans ce groupe."); break; }
                setAssignChequeGroup(aAssigner);
                setDecaissement(null);
                setModalAssignCheque(true);
                break;
            }
            case "ordreDepenseGroup":
                showOrdreDepense(aggregateGroupe(record._lignes));
                break;
            case "ficheGroup":
                showFicheDemandeReglement(aggregateGroupe(record._lignes));
                break;
            case "quittanceGroup":
                showQuittance(aggregateGroupe(record._lignes));
                break;
            case "remiseGroup": {
                // Ligne du lot portant le chèque assigné (le backend encaisse tout le lot du chèque).
                const ligne = (record._lignes || []).find((l) => l?.etatDecaissement === "EN_ATTENTE_D_ENCAISSEMENT" && l?.numeroCheque)
                    || (record._lignes || []).find((l) => l?.numeroCheque)
                    || record._lignes?.[0];
                setDecaissement(ligne);
                setModalRemiseCheque(true);
                break;
            }
            case "annulerChequeGroup": {
                const ligne = (record._lignes || []).find((l) => l?.etatDecaissement === "EN_ATTENTE_D_ENCAISSEMENT" && l?.numeroCheque)
                    || (record._lignes || []).find((l) => l?.numeroCheque)
                    || record._lignes?.[0];
                setDecaissement(ligne);
                setModalAnnulerCheque(true);
                break;
            }
            case "ventilerGarantie": {
                const l = record?._lignes?.[0];
                setVentilationTarget({ codeId: l?.codeId, montantGlobal: record?.montantGlobal, ventilation: l?.ventilation });
                setVentilationOpen(true);
                break;
            }
            default:
                break;
        }
    };

    // Menu d'un règlement GROUPÉ : basé sur les ÉTATS DES LIGNES (et non sur l'état agrégé « GROUPE »,
    // posé dès que les lignes diffèrent — ce qui masquait à tort Valider / Assigner un chèque / etc.).
    const getGroupMenuItems = (record) => {
        const lignes = record?._lignes || [];
        const some = (etat) => lignes.some((l) => l?.etatDecaissement === etat);
        const hasEnAttente = some("EN_ATTENTE");
        const hasValide = some("VALIDE");
        const hasAttEncaissement = some("EN_ATTENTE_D_ENCAISSEMENT");
        const hasEncaisse = some("ENCAISSE");
        const hasNonRejete = lignes.some((l) => l?.etatDecaissement !== "REJETE");
        const items = [];
        if (hasEnAttente) {
            items.push({ key: "validateGroup", icon: <CheckCircleOutlined style={{ color: "green" }} />, label: "Valider le groupe" });
            items.push({ key: "rejectGroup", icon: <CloseCircleOutlined style={{ color: "red" }} />, label: "Rejeter le groupe" });
        }
        if (hasValide) {
            items.push({ key: "assignChequeGroup", icon: <DollarOutlined />, label: "Assigner un chèque (groupe)" });
        }
        // Documents (ordre de dépense / fiche) toujours consultables sur un groupe.
        items.push({ key: "ordreDepenseGroup", icon: <PrinterOutlined />, label: "Ordre de dépense groupé" });
        items.push({ key: "ficheGroup", icon: <FileTextOutlined />, label: "Fiche de demande groupée" });
        // Chèque assigné → quittance + remise de chèque (encaissement), comme pour un règlement simple.
        if (hasAttEncaissement || hasEncaisse) {
            items.push({ key: "quittanceGroup", icon: <FileTextOutlined />, label: "Quittance groupée" });
        }
        if (hasAttEncaissement) {
            items.push({ key: "remiseGroup", icon: <CheckCircleOutlined style={{ color: "green" }} />, label: "Remise de chèque" });
            items.push({ key: "annulerChequeGroup", icon: <CloseCircleOutlined style={{ color: "red" }} />, label: "Annuler le chèque" });
        }
        // Suivi par garantie : ventiler ce règlement par garantie (dès qu'une ligne n'est pas rejetée).
        if (hasNonRejete) {
            items.push({ key: "ventilerGarantie", icon: <FileTextOutlined />, label: "Ventiler par garantie" });
        }
        // Annulation technique du GROUPE (HSI015) : sur VALIDE/CHEQUE_ANNULE (comme en unitaire).
        if (canAnnulerTech && (some("VALIDE") || some("CHEQUE_ANNULE"))) {
            items.push({
                key: "annulerReglementTechniqueGroupe",
                icon: <CloseCircleOutlined style={{ color: "#a8071a" }} />,
                label: "Annuler le règlement (technique)",
            });
        }
        return items;
    };

    const handleEditDecaissement = (item) => {
        if (readOnly) { toast.warn("Dossier en lecture seule : action impossible."); return; }
        if (!item?.codeId) {
            return;
        }
        setEditDecaissementId(item.codeId);
        setEditModalOpen(true);
    };

    const handleEditModalClose = () => {
        setEditModalOpen(false);
        setEditDecaissementId(null);
    };

    const handleDecaissementUpdated = (updatedDecaissement) => {
        const shouldRefetch =
            typeof onDecaissementUpdated === "function"
                ? onDecaissementUpdated(updatedDecaissement) !== false
                : true;

        if (!shouldRefetch) {
            return;
        }

        if (typeof setRefetch === "function") {
            setRefetch((prev) => !prev);
        } else {
            window.location.reload();
        }
    };

    const showQuittance = (item) => {
        setQuittance(item);
        setModalQuittance(true);
    };

    const showFicheDemandeReglement = (item) => {
        setFicheDemandeReglement(item);
        setModalFicheDemandeReglement(true);
    };

    const showOrdreDepense = (item) => {
        setOrdreDepense(item);
        setOdMotif(item?.motifReglement || "");
        setModalOrdreDepense(true);
    };

    // Enregistre le motif saisi depuis l'ordre de dépense. Règlement multiple → applique à tout le
    // groupe (groupeReglementCodeId) ; règlement simple → au seul décaissement. HSI089 côté back.
    const enregistrerMotifOD = async () => {
        if (readOnly) { toast.warn("Dossier en lecture seule : action impossible."); return; }
        const grp = ordreDepense?.groupeReglementCodeId;
        const estMultiple = !!(ordreDepense?.isReglementMultiple && grp);
        try {
            setSavingOdMotif(true);
            if (estMultiple) {
                await updateMotifReglementGroupe(grp, odMotif);
            } else if (ordreDepense?.codeId) {
                await updateMotifReglement(ordreDepense.codeId, odMotif);
            } else {
                toast.warn("Règlement introuvable pour l'enregistrement du motif.");
                return;
            }
            // Reflète le motif dans l'aperçu immédiatement.
            setOrdreDepense((prev) => ({ ...prev, motifReglement: odMotif }));
            toast.success("Motif du règlement enregistré.");
            if (typeof setRefetch === "function") setRefetch((p) => !p);
        } catch (e) {
            toast.error(e?.response?.data?.message || e?.response?.data?.error
                || "Erreur lors de l'enregistrement du motif.");
        } finally {
            setSavingOdMotif(false);
        }
    };

    // const showRemiseCheque = (item) => {
    //     setQuittance(item);
    //     setModalRemiseCheque(true);
    // };

    const handleMenuClick = (e, record) => {
        // Gel total : seules les actions de LECTURE (documents) restent possibles en lecture seule.
        const readOnlyKeys = ["quittance", "ordreDepense", "ficheDemandeReglement"];
        if (readOnly && !readOnlyKeys.includes(e.key)) {
            toast.warn("Dossier en lecture seule : action impossible.");
            return;
        }
        switch (e.key) {
            case "edit":
                handleEditDecaissement(record);
                break;
            case "validate":
                handleValiderDecaissement(record);
                break;
            case "reject":
                handleRejeterDecaissement(record);
                break;
            case "annulerReglementTechnique":
                handleAnnulerReglementTechnique(record);
                break;
            case "editMotif":
                setEditMotifRecord(record);
                break;
            case "quittance":
                showQuittance(record);
                break;
            case "ordreDepense":
                showOrdreDepense(record);
                break;
            case "ficheDemandeReglement":
                showFicheDemandeReglement(record);
                break;
            case "assignCheque":
                setChequeEditMode(false);
                setAssignChequeGroup(null);
                setDecaissement(record);
                setModalAssignCheque(true);
                break;
            case "modifierCheque":
                setChequeEditMode(true);
                setAssignChequeGroup(null);
                setDecaissement(record);
                setModalAssignCheque(true);
                break;
            case "remiseDeCheque":
                setDecaissement(record);
                setModalRemiseCheque(true);
                break;
            case "annulerCheque":
                setDecaissement(record);
                setModalAnnulerCheque(true);
                break;
            default:
                break;
        }
    };


    const getStateColor = (state) => {
        switch (state) {
            case "VALIDE":
                return { color: "success", label: "✓ Validé" };
            case "EN_ATTENTE":
                return { color: "warning", label: "⏳ En attente" };
            case "EN_ATTENTE_D_ENCAISSEMENT":
                return { color: "processing", label: "📬 En attente d'encaissement" };
            case "ENCAISSE":
                return { color: "success", label: "✓ Encaissé" };
            case "CHEQUE_ANNULE":
                return { color: "error", label: "✗ Chèque annulé" };
            case "REJETE":
                return { color: "error", label: "✗ Rejeté" };
            case "GROUPE":
                return { color: "processing", label: "● États mixtes" };
            default:
                return { color: "default", label: state };
        }
    };

    const getAlertConfig = () => {
        if (!evaluationCourante) {
            return {
                type: "info",
                message: "Aucune évaluation n'a encore été effectuée",
                icon: "ℹ️"
            };
        }
        if (evaluationCourante?.etatEvaluation === "EN_ATTENTE") {
            return {
                type: "warning",
                message: "L'évaluation courante est en attente de validation",
                icon: "⏳"
            };
        }
        if (evaluationCourante?.etatEvaluation === "REJETE") {
            return {
                type: "error",
                message: "L'évaluation courante a été rejetée",
                icon: "✗"
            };
        }
        return null;
    };

    const alertConfig = getAlertConfig();

    // Cellule de la colonne Date (réutilisée : date OD en cours de paiement / date chèque en payés).
    const renderDateCell = (date) => (
        <div>
            <div style={{ fontWeight: 500, color: "#1a1a1a", fontSize: "13px" }}>
                <CalendarOutlined style={{ marginRight: "6px", color: "#4f46e5" }} />
                {frenchDate(date)}
            </div>
        </div>
    );

    const columns = [
        {
            title: "Bénéficiaire",
            dataIndex: "beneficiaire",
            key: "beneficiaire",
            width: 250,
            render: (beneficiaire, record) => {
                // Ligne enfant d'un groupe : on affiche la nature plutôt que de répéter le bénéficiaire.
                if (record?.isGroupChild) {
                    return (
                        <div style={{ paddingLeft: 28, color: "#374151", display: "flex", alignItems: "center", gap: 8 }}>
                            <span style={{ color: "#9ca3af" }}>↳</span>
                            <span style={{ fontWeight: 500 }}>{natureLabel(record?.nature)}</span>
                        </div>
                    );
                }
                const neg = Number(record?.montantGlobal) < 0;
                const nomComplet = `${beneficiaire?.nom || ""} ${beneficiaire?.prenom || ""}`.trim() || (neg ? "Contre-passation" : "—");
                if (isV2) {
                    return (
                        <div className="rbenef">
                            <span className={`av ${record?.isGroupParent ? "grp" : ""} ${neg ? "neg" : ""}`}>
                                {neg ? "↩" : (beneficiaire?.nom?.charAt(0) || (record?.isGroupParent ? "G" : "B"))}
                            </span>
                            <div>
                                <div className="nm">
                                    {nomComplet}
                                    {record?.isGroupParent && (
                                        <Tag color="purple" style={{ margin: 0, fontSize: 11 }}>Groupé · {record?._lignes?.length}</Tag>
                                    )}
                                    {record?.libelle?.toLowerCase().includes("reprise") && (
                                        <Tag style={{ margin: 0, fontSize: 11, background: "rgba(250,173,20,0.2)", color: "#d48806", border: "none" }}>Reprise</Tag>
                                    )}
                                </div>
                                {record?.numero && <div className="rg">Reg. N° {record.numero}</div>}
                            </div>
                        </div>
                    );
                }
                return (
                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                    <div style={{
                        width: "36px",
                        height: "36px",
                        borderRadius: "50%",
                        backgroundColor: record?.isGroupParent ? "#7c3aed" : "#4f46e5",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "white",
                        fontSize: "14px",
                        fontWeight: 600
                    }}>
                        {beneficiaire?.nom?.charAt(0) || "B"}
                    </div>
                    <div>
                        <div style={{ fontWeight: 600, color: "#1a1a1a" }}>
                            {`${beneficiaire?.nom} ${beneficiaire?.prenom || ""}`.trim()}
                            {record?.isGroupParent && (
                                <Tag color="purple" style={{ margin: "4px", padding: "2px 8px", fontSize: "11px", fontWeight: 600 }}>
                                    Groupé · {record?._lignes?.length} natures
                                </Tag>
                            )}
                            {record?.numero && (
                                <Tag style={{ margin: "4px", padding: "2px 8px", fontSize: "11px", fontWeight: 500, backgroundColor: "#f3f4f6", color: "#4b5563", border: "1px solid #e5e7eb" }}>
                                    Reg. N° {record.numero}
                                </Tag>
                            )}
                            {record?.libelle?.toLowerCase().includes('reprise') && (
                                <Tag style={{ margin: "4px", padding: "2px 8px", fontSize: "11px", fontWeight: 700, background: 'rgba(250,173,20,0.2)', color: '#faad14', border: '1px solid #faad14', borderRadius: '6px' }}>
                                    📥 REPRISE
                                </Tag>
                            )}
                        </div>
                    </div>
                </div>
                );
            },
        },
        {
            title: "Chèque",
            dataIndex: "numeroCheque",
            key: "numeroCheque",
            width: 160,
            render: (numeroCheque, record) => {
                if (!numeroCheque) {
                    return <span style={{ color: "#9ca3af" }}>—</span>;
                }
                return (
                    <div>
                        <div style={{ fontWeight: 600, color: "#1a1a1a", fontSize: "13px" }}>
                            N° {numeroCheque}
                        </div>
                        {record?.banque && (
                            <div style={{ fontSize: "12px", color: "#6b7280" }}>{record.banque}</div>
                        )}
                    </div>
                );
            },
        },
        {
            title: "Montant TTC",
            dataIndex: "montantGlobal",
            key: "montantGlobal",
            width: 140,
            render: (montantGlobal) => {
                const neg = Number(montantGlobal) < 0;
                if (isV2) {
                    return (
                        <div className="rmoney" style={{ color: neg ? "#DC2626" : "#0E9F6E" }}>
                            {neg ? "−" : ""}{displayNumbers(Math.trunc(Math.abs(montantGlobal)))} <br />
                            <span className="cur">FCFA</span>
                        </div>
                    );
                }
                return (
                    <div style={{ fontWeight: 600, color: "#059669" }}>
                        {displayNumbers(Math.trunc(montantGlobal))} <br />
                        <span style={{ fontSize: "11px", color: "#6b7280", fontWeight: 400 }}>FCFA</span>
                    </div>
                );
            },
        },
        {
            title: "Montant retenu",
            dataIndex: "taxes",
            key: "taxes",
            width: 130,
            render: (taxes) => (
                <div style={{ fontWeight: 600, color: "#dc2626" }}>
                    {displayNumbers(Math.trunc(taxes || 0))} <br />
                    <span style={{ fontSize: "11px", color: "#6b7280", fontWeight: 400 }}>FCFA</span>
                </div>
            ),
        },
        {
            // Colonne Date : « ordre de dépense » (dateReglement) en cours de paiement, et « chèque »
            // (dateEmissionCheque) dans l'onglet Payés — cf. surcharge dans renderReglementsTable('payes').
            title: "Date",
            dataIndex: "dateReglement",
            key: "dateReglement",
            width: 160,
            render: (dateReglement) => renderDateCell(dateReglement),
        },
        {
            title: "État",
            dataIndex: "etatDecaissement",
            key: "etatDecaissement",
            width: 160,
            render: (etatDecaissement) => {
                if (isV2) {
                    const c = RGTAB_V2_STATE[etatDecaissement] || { label: etatDecaissement || "—", cls: "default" };
                    return <span className={`rp ${c.cls}`}><span className="d"></span>{c.label}</span>;
                }
                const stateConfig = getStateColor(etatDecaissement);
                return (
                    <Tag
                        color={stateConfig.color}
                        style={{
                            padding: "6px 12px",
                            borderRadius: "6px",
                            fontSize: "12px",
                            fontWeight: 500,
                            border: "none"
                        }}
                    >
                        {stateConfig.label}
                    </Tag>
                );
            },
        },
        {
            title: "Motif",
            dataIndex: "motif",
            key: "motif",
            width: 200,
            render: (_, record) => {
                // CHAQUE LIGNE SON MOTIF (traçabilité) : ligne ANNULÉE / contre-passation (état
                // CHEQUE_ANNULE) → motif d'ANNULATION ; toute autre ligne (paiement, reprise…) → motif
                // du RÈGLEMENT initial. Le motif d'annulation ne doit JAMAIS masquer le motif initial
                // d'une ligne encaissée (dont le chèque a été annulé). À défaut : « — ».
                const estAnnulee = record?.etatDecaissement === "CHEQUE_ANNULE";
                const motif = estAnnulee
                    ? (record?.motifAnnulerCheque || record?.motifReglement)
                    : record?.motifReglement;
                if (!motif) {
                    return <span style={{ color: "#9ca3af" }}>—</span>;
                }
                return (
                    <div style={{ fontSize: "12px", color: "#4b5563", whiteSpace: "normal", lineHeight: 1.35 }}>
                        {motif}
                    </div>
                );
            },
        },
        {
            title: "Actions",
            key: "actions",
            width: 60,
            fixed: "right",
            align: "center",
            render: (_, record) => {
                // Lignes enfants d'un groupe : seule l'annulation technique UNITAIRE est proposée
                // (HSI015, sur VALIDE/CHEQUE_ANNULE) ; le reste des actions se pilote au niveau du groupe.
                if (record?.isGroupChild) {
                    const etatLigne = record?.etatDecaissement;
                    if (canAnnulerTech && (etatLigne === "VALIDE" || etatLigne === "CHEQUE_ANNULE") && !readOnly) {
                        return (
                            <Dropdown
                                menu={{
                                    items: [{
                                        key: "annulerReglementTechnique",
                                        icon: <CloseCircleOutlined style={{ color: "#a8071a" }} />,
                                        label: "Annuler le règlement (technique)",
                                    }],
                                    onClick: () => handleAnnulerReglementTechnique(record),
                                }}
                                trigger={['click']}
                            >
                                <Button type="text" icon={<MoreOutlined />} style={{ cursor: "pointer", fontSize: "18px" }} />
                            </Dropdown>
                        );
                    }
                    return <span style={{ color: "#d1d5db" }}>—</span>;
                }
                const isGroup = record?.isGroupParent;
                const menuItems = isGroup ? getGroupMenuItems(record) : getMenuItems(record);
                if (!menuItems.length) {
                    return <span style={{ color: "#d1d5db" }}>—</span>;
                }
                return (
                    <Dropdown
                        menu={{
                            items: menuItems,
                            onClick: (e) => (isGroup ? handleGroupMenuClick(e, record) : handleMenuClick(e, record)),
                        }}
                        trigger={['click']}
                    >
                        <Button
                            type="text"
                            icon={<MoreOutlined />}
                            style={{ cursor: "pointer", fontSize: "18px" }}
                        />
                    </Dropdown>
                );
            },
        },
    ];

    const getMenuItems = (record) => {
        const etat = record?.etatDecaissement;
        const items = [];

        // Édition : uniquement avant validation
        if (etat === "EN_ATTENTE") {
            items.push({
                key: "edit",
                icon: <i className="bx bx-edit" />,
                label: "Éditer",
            });
        }

        // EN_ATTENTE → valider ou rejeter
        if (etat === "EN_ATTENTE") {
            items.push({
                key: "validate",
                icon: <CheckCircleOutlined style={{ color: "green" }} />,
                label: "Valider",
            });
            items.push({
                key: "reject",
                icon: <CloseCircleOutlined style={{ color: "red" }} />,
                label: "Rejeter",
            });
        }

        // VALIDE → assigner un chèque, imprimer documents, annuler le règlement
        if (etat === "VALIDE") {
            if (items.length) items.push({ type: "divider" });
            items.push({
                key: "assignCheque",
                icon: <DollarOutlined />,
                label: "Assigner Chèque",
            });
            items.push({
                key: "ordreDepense",
                icon: <PrinterOutlined />,
                label: "Ordre de Dépense",
            });
            items.push({
                key: "ficheDemandeReglement",
                icon: <FileTextOutlined />,
                label: "Fiche Demande",
            });
            items.push({
                key: "reject",
                icon: <CloseCircleOutlined style={{ color: "red" }} />,
                label: "Annuler le règlement",
            });
            // Modification du seul motif après validation — sous habilitation.
            if (canEditMotif) {
                items.push({
                    key: "editMotif",
                    icon: <i className="bx bx-edit" />,
                    label: "Modifier le motif",
                });
            }
            // Annulation technique : visible UNIQUEMENT sur VALIDE et CHEQUE_ANNULE.
            if (canAnnulerTech) {
                items.push({
                    key: "annulerReglementTechnique",
                    icon: <CloseCircleOutlined style={{ color: "#a8071a" }} />,
                    label: "Annuler le règlement (technique)",
                });
            }
        }

        // EN_ATTENTE_D_ENCAISSEMENT → remise OU annulation du chèque + impressions
        if (etat === "EN_ATTENTE_D_ENCAISSEMENT") {
            if (items.length) items.push({ type: "divider" });
            items.push({
                key: "quittance",
                icon: <FileTextOutlined />,
                label: "Quittance",
            });
            items.push({
                key: "ordreDepense",
                icon: <PrinterOutlined />,
                label: "Ordre de Dépense",
            });
            items.push({
                key: "ficheDemandeReglement",
                icon: <FileTextOutlined />,
                label: "Fiche Demande",
            });
            items.push({
                key: "remiseDeCheque",
                icon: <CheckCircleOutlined style={{ color: "green" }} />,
                label: "Remise de chèque",
            });
            items.push({
                key: "annulerCheque",
                icon: <CloseCircleOutlined style={{ color: "red" }} />,
                label: "Annuler le chèque",
            });
            // Modifier les informations du chèque assigné — sous habilitation HSI089 (même que le motif).
            if (canEditMotif) {
                items.push({
                    key: "modifierCheque",
                    icon: <i className="bx bx-edit" />,
                    label: "Modifier le chèque",
                });
            }
        }

        // ENCAISSE → consultations + annulation du chèque
        if (etat === "ENCAISSE") {
            if (items.length) items.push({ type: "divider" });
            items.push({
                key: "quittance",
                icon: <FileTextOutlined />,
                label: "Quittance",
            });
            items.push({
                key: "ordreDepense",
                icon: <PrinterOutlined />,
                label: "Ordre de Dépense",
            });
            items.push({
                key: "ficheDemandeReglement",
                icon: <FileTextOutlined />,
                label: "Fiche Demande",
            });
            items.push({
                key: "annulerCheque",
                icon: <CloseCircleOutlined style={{ color: "red" }} />,
                label: "Annuler le chèque",
            });
        }

        // CHEQUE_ANNULE → état terminal côté chèque ; on peut éventuellement
        // réassigner un nouveau chèque (à confirmer côté backend) + consulter.
        if (etat === "CHEQUE_ANNULE") {
            if (items.length) items.push({ type: "divider" });
            items.push({
                key: "assignCheque",
                icon: <DollarOutlined />,
                label: "Réassigner un nouveau chèque",
            });
            items.push({
                key: "ordreDepense",
                icon: <PrinterOutlined />,
                label: "Ordre de Dépense",
            });
            items.push({
                key: "ficheDemandeReglement",
                icon: <FileTextOutlined />,
                label: "Fiche Demande",
            });
            if (canAnnulerTech) {
                items.push({ type: "divider" });
                items.push({
                    key: "annulerReglementTechnique",
                    icon: <CloseCircleOutlined style={{ color: "#a8071a" }} />,
                    label: "Annuler le règlement (technique)",
                });
            }
        }

        // REJETE → état terminal, aucune action
        // (on peut laisser le menu vide ou ne pas afficher le bouton "..." du tout)

        return items;
    };


    // Table des règlements, réutilisée par les deux sous-onglets (en cours / payés).
    // variant "payes" → la colonne Date affiche la date du CHÈQUE (dateEmissionCheque) ; sinon la
    // date de l'ordre de dépense (dateReglement).
    const renderReglementsTable = (data, variant) => {
        const cols = variant === "payes"
            ? columns.map((c) => (c.key === "dateReglement"
                ? { ...c, title: "Date chèque", render: (_v, record) => renderDateCell(record?.dateEmissionCheque) }
                : c))
            : columns;
        return (
        <Table
            dataSource={data}
            columns={cols}
            rowKey="codeId"
            expandable={{ indentSize: 0 }}
            pagination={{
                pageSize: 10,
                showSizeChanger: true,
                showQuickJumper: true,
                total: data.length,
                showTotal: (total) => `Total: ${total} règlement(s)`
            }}
            style={{ fontSize: "13px" }}
            rowClassName={(record) => (record?.isGroupParent ? "group-row" : "")}
            locale={{ emptyText: "Aucun règlement dans cette catégorie" }}
        />
        );
    };

    // Libellé d'onglet : intitulé + compteur + montant total de la catégorie.
    const paymentTabLabel = (text, rows, color) => (
        <span style={{ fontWeight: 500 }}>
            {text}
            <Tag color={color} style={{ marginInlineStart: 8, marginInlineEnd: 4 }}>{rows.length}</Tag>
            {rows.length > 0 && (
                <span style={{ color: "#6b7280", fontWeight: 400 }}>
                    {displayNumbers(Math.trunc(sumMontant(rows)))} F
                </span>
            )}
        </span>
    );

    return (
        <div
            className={isV2 ? "rgtab-v2" : ""}
            style={isV2 ? { padding: 0 } : { padding: "24px", backgroundColor: "#f5f7fa", minHeight: "100vh" }}
        >
            {isV2 && <style>{RGTAB_V2_CSS}</style>}
            {/* Header Section */}
            <div style={{
                marginBottom: "24px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                gap: "16px"
            }}>
                <div>
                    <h2 style={{ margin: 0, fontSize: "24px", fontWeight: 600, color: "#1a1a1a" }}>
                        Règlements
                    </h2>
                    <p style={{ margin: "4px 0 0 0", color: "#6b7280", fontSize: "14px" }}>
                        {visibleDecaissements?.length || 0} règlement(s) actif(s)
                        {rejectedCount > 0 && (
                            <span style={{ marginLeft: 8, color: "#9ca3af" }}>
                                · {rejectedCount} rejeté(s) {showRejected ? "affiché(s)" : "masqué(s)"}
                            </span>
                        )}
                    </p>
                </div>
                <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", alignItems: "center" }}>
                    {/* Afficher / masquer les règlements rejetés (masqués par défaut) */}
                    {rejectedCount > 0 && (
                        <div style={{ display: "flex", alignItems: "center", gap: 8, height: 44, paddingRight: 4 }}>
                            <Switch size="small" checked={showRejected} onChange={setShowRejected} />
                            <span style={{ fontSize: 13, color: "#6b7280" }}>Afficher les rejetés</span>
                        </div>
                    )}
                    {/* Boutons d'export — visibles uniquement s'il y a des données */}
                    {visibleDecaissements?.length > 0 && (
                        <>
                            <Button
                                icon={<FilePdfOutlined />}
                                onClick={() => generateReglementsPDF(visibleDecaissements, sinistre)}
                                style={{
                                    height: "44px",
                                    borderRadius: "8px",
                                    fontWeight: 500,
                                    borderColor: "#dc2626",
                                    color: "#dc2626",
                                }}
                            >
                                Exporter PDF
                            </Button>
                            <Button
                                icon={<FileExcelOutlined />}
                                onClick={() => generateReglementsExcel(visibleDecaissements, sinistre)}
                                style={{
                                    height: "44px",
                                    borderRadius: "8px",
                                    fontWeight: 500,
                                    borderColor: "#059669",
                                    color: "#059669",
                                }}
                            >
                                Exporter Excel
                            </Button>
                        </>
                    )}
                    {evaluationCourante?.etatEvaluation === "VALIDE" && (
                        <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            size="large"
                            disabled={readOnly}
                            onClick={() => { if (readOnly) { toast.warn("Dossier en lecture seule : action impossible."); return; } setReglementModalOpen(true); }}
                            style={{
                                backgroundColor: '#242166',
                                borderColor: '#242166',
                                height: "44px",
                                borderRadius: "8px",
                                fontWeight: 500,
                                boxShadow: "0 2px 8px rgba(79, 70, 229, 0.3)"
                            }}
                        >
                            Nouveau Règlement
                        </Button>
                    )}
                </div>
            </div>

            {/* Alert Section */}
            {alertConfig && (
                <div style={{ marginBottom: "20px" }}>
                    <Alert
                        message={alertConfig.message}
                        type={alertConfig.type}
                        showIcon
                        style={{
                            borderRadius: "8px",
                            border: "none",
                            fontSize: "14px"
                        }}
                    />
                </div>
            )}

            {/* Table or Empty State */}
            {decaissements?.length ? (
                <Card
                    style={{
                        borderRadius: "12px",
                        border: "1px solid #e5e7eb",
                        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.06)",
                        overflow: "hidden"
                    }}
                >
                    <Tabs
                        activeKey={paymentTab}
                        onChange={setPaymentTab}
                        type="card"
                        items={[
                            {
                                key: "en-cours",
                                label: paymentTabLabel("En cours de paiement", enCoursData, "orange"),
                                children: renderReglementsTable(enCoursData, "en-cours"),
                            },
                            {
                                key: "payes",
                                label: paymentTabLabel("Payés", payesData, "green"),
                                children: renderReglementsTable(payesData, "payes"),
                            },
                        ]}
                    />
                    <style>{`
            .group-row > td { background-color: #faf9ff !important; font-weight: 500; }
            .ant-table tbody > tr:hover > td { background-color: #f3f4f6 !important; }
          `}</style>
                </Card>
            ) : (
                <Card
                    style={{
                        borderRadius: "12px",
                        border: "2px dashed #d1d5db",
                        textAlign: "center",
                        padding: "60px 20px",
                        background: "#ffffff"
                    }}
                >
                    <DollarOutlined style={{ fontSize: "64px", color: "#d1d5db", marginBottom: "16px" }} />
                    <h3 style={{ fontSize: "18px", fontWeight: 600, color: "#4b5563", margin: "0 0 8px 0" }}>
                        Aucun règlement effectué
                    </h3>
                    <p style={{ color: "#6b7280", margin: 0 }}>
                        Aucun décaissement n'a encore été enregistré pour ce dossier sinistre
                    </p>
                </Card>
            )}
            <Modal
                title={`QUITTANCE D'INDEMNITE - SINISTRE N° ${sinistre?.numSinistre}`}
                centered
                open={modalQuittance}
                onOk={() => setModalQuittance(false)}
                onCancel={() => setModalQuittance(false)}
                width={850}
            >
                <div id="quittanceSinistre" ref={printQReference}>
                    <QuittanceIndemnite decaissement={quittance} sinistre={sinistre} />
                </div>
                <AntdButton
                    onClick={exportQToPdf}
                    type="primary"
                    style={{ background: "blue" }}
                    icon={<PrinterOutlined />}
                >
                    Imprimer
                    <br />
                    <br />
                    <br />
                </AntdButton>
            </Modal>

            <Modal
                title={`FICHE DE DEMANDE DE REGLEMENT - SINISTRE N° ${sinistre?.numSinistre}`}
                centered
                open={modalFicheDemandeReglement}
                onOk={() => setModalFicheDemandeReglement(false)}
                onCancel={() => setModalFicheDemandeReglement(false)}
                width={850}
            >
                <div id="ficheDemandeReglement" ref={printFDReference}>
                    <FicheDemandeReglementSinistre
                        decaissement={ficheDemandeReglement}
                        sinistre={sinistre}
                    />
                </div>
                <Button
                    onClick={exportFDRToPdf}
                    type="primary"
                    style={{ background: "gray" }}
                    icon={<PrinterOutlined />}
                >
                    Imprimer
                    <br />
                    <br />
                    <br />
                </Button>
            </Modal>

            <Modal
                title={`ORDRE DE DÉPENSE - SINISTRE N° ${sinistre?.numSinistre || sinistre?.numeroSinistrem || sinistre?.numeroSinistreM}`}
                centered
                open={modalOrdreDepense}
                onOk={() => setModalOrdreDepense(false)}
                onCancel={() => setModalOrdreDepense(false)}
                width={850}
            >
                {/* Saisie du motif du règlement (règlements multiples surtout) : modifiable tant que
                    le règlement est VALIDÉ (HSI089), sauf paiement d'un établissement (motif imposé). */}
                {canEditMotif && ordreDepense?.etatDecaissement === "VALIDE" && !ordreDepense?.beneficiaireMoral && (
                    <div style={{ marginBottom: 12, padding: "10px 12px", background: "#f5f7ff", border: "1px solid #d6ddff", borderRadius: 8 }}>
                        <div style={{ fontWeight: 600, marginBottom: 6, color: "#242166" }}>
                            Motif du règlement{ordreDepense?.isReglementMultiple ? " (appliqué à tous les règlements du groupe)" : ""}
                        </div>
                        <Input.TextArea
                            rows={2}
                            value={odMotif}
                            onChange={(e) => setOdMotif(e.target.value)}
                            placeholder="Saisir le motif du règlement…"
                        />
                        <div style={{ marginTop: 8, textAlign: "right" }}>
                            <Button type="primary" loading={savingOdMotif} disabled={readOnly} onClick={enregistrerMotifOD} style={{ background: "#242166" }}>
                                Enregistrer le motif
                            </Button>
                        </div>
                    </div>
                )}
                <div id="ordreDepense" ref={printODReference}>
                    <OrdreDeDepense decaissement={ordreDepense} sinistre={sinistre} />
                </div>
                <Button
                    onClick={exportODToPdf}
                    type="primary"
                    style={{ background: "blue" }}
                    icon={<PrinterOutlined />}
                >
                    Imprimer
                    <br />
                    <br />
                    <br />
                </Button>
            </Modal>

            <AssignCheque
                open={modalAssignCheque}
                decaissement={decaissement}
                decaissements={assignChequeGroup}
                sinistre={sinistre}
                editMode={chequeEditMode}
                onCancel={() => setModalAssignCheque(false)}
            />
            <RemiseCheque
                open={modalRemiseCheque}
                decaissement={decaissement}
                sinistre={sinistre}
                onCancel={() => setModalRemiseCheque(false)}
                onSuccess={(updated) => {
                    // Encaissement réussi → le règlement passe à ENCAISSE : on bascule sur
                    // « Payés » et on rafraîchit la liste EN PLACE (handleDecaissementUpdated,
                    // sans window.location.reload) pour que la bascule d'onglet tienne.
                    setModalRemiseCheque(false);
                    setPaymentTab("payes");
                    handleDecaissementUpdated(updated);
                }}
            />
            <AnnulerCheque
                open={modalAnnulerCheque}
                decaissement={decaissement}
                sinistre={sinistre}
                onCancel={() => setModalAnnulerCheque(false)}
            />
            <Modal
                open={techCancel.open}
                onCancel={() => setTechCancel({ open: false, mode: null, record: null })}
                onOk={confirmTechCancel}
                okText={techCancel.mode === "group" ? "Annuler le groupe" : "Annuler le règlement"}
                cancelText="Fermer"
                okButtonProps={{ danger: true }}
                confirmLoading={techSaving}
                destroyOnClose
                width={640}
                title={<span><CloseCircleOutlined style={{ color: "darkred", marginRight: 8 }} />
                    {techCancel.mode === "group" ? "Annuler techniquement tout le groupe" : "Annuler techniquement ce règlement"}</span>}
            >
                <div style={{ marginBottom: 12 }}>
                    <p style={{ marginBottom: 4 }}>Une <b>ligne négative</b> (contre-écriture) sera créée
                        {techCancel.mode === "group" ? " pour chaque règlement du groupe" : ""}, et le(s) règlement(s)
                        d'origine passeront en <b>REJETÉ</b>.</p>
                    <p style={{ color: "#888", marginBottom: 0 }}>Aucune écriture comptable n'est régénérée.
                        Action tracée (auteur, date, motif). Un chèque encore actif doit être annulé au préalable.</p>
                </div>
                <div style={{ marginBottom: 12 }}>
                    <label className="form-label fw-bold">Motif de l'annulation <span style={{ color: "red" }}>*</span></label>
                    <Select
                        style={{ width: "100%" }}
                        showSearch
                        optionFilterProp="label"
                        placeholder="Sélectionner un motif"
                        value={techMotif}
                        onChange={setTechMotif}
                        options={techMotifs}
                        allowClear
                    />
                </div>
                <div>
                    <label className="form-label">Précision (facultative)</label>
                    <Input.TextArea
                        rows={2}
                        value={techPrecision}
                        onChange={(e) => setTechPrecision(e.target.value)}
                        placeholder="Détail complémentaire (optionnel)"
                    />
                </div>
            </Modal>
            <ReglementModal
                sinistre={sinistre}
                modalControl={reglementControl}
                sap={sinistre?.sap}
                sapMin={1000}
            />
            <EditDecaissementModal
                codeId={editDecaissementId}
                sinistre={sinistre}
                open={editModalOpen}
                onClose={handleEditModalClose}
                onUpdated={handleDecaissementUpdated}
            />
            <VentilationGarantieModal
                open={ventilationOpen}
                onClose={() => setVentilationOpen(false)}
                onSaved={() => { if (typeof setRefetch === "function") setRefetch((p) => !p); }}
                decaissement={ventilationTarget}
                sinistreCodeId={sinistre?.codeId}
            />
            <EditMotifReglementModal
                open={!!editMotifRecord}
                decaissement={editMotifRecord}
                onClose={() => setEditMotifRecord(null)}
                onSaved={() => { setEditMotifRecord(null); window.location.reload(); }}
            />
            {fixCategorieData && (
                <FixEnginCategorieModal
                    open={!!fixCategorieData}
                    enginCodeId={fixCategorieData.enginCodeId}
                    immatriculation={fixCategorieData.immatriculation}
                    onFixed={async () => {
                        const { decaissementCodeId: codeId, dateReglement } = fixCategorieData;
                        setFixCategorieData(null);
                        try {
                            await validateDecaissement(codeId, dateReglement);
                            toast.success('Décaissement validé avec succès');
                            window.location.reload();
                        } catch (e) {
                            toast.error(messageRefusAntidatage(e) || "Erreur lors de la validation après correction");
                        }
                    }}
                    onClose={() => setFixCategorieData(null)}
                />
            )}
        </div>
    );
};

export default ReglementsTab;
